import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import FloatingWhatsApp from '@/components/feature/FloatingWhatsApp';
import { WHATSAPP_URL } from '@/constants/whatsapp';
import { getOrganizationSchema, getWebPageSchema, SCHEMA_SITE_URL } from '@/constants/schema';

const SITE_URL = import.meta.env.VITE_SITE_URL as string;

const PAGE_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    getOrganizationSchema(),
    getWebPageSchema({
      path: '/industrial-gloves',
      name: 'Industrial Gloves Wholesale Oman | Heavy Duty PPE Gloves | omandisposables.com',
      description: 'Heavy duty industrial PPE gloves wholesale for factories in Oman & GCC. Cut resistant, chemical barrier, anti-static ESD. Bulk supply from Oman.',
      breadcrumbId: `${SCHEMA_SITE_URL}/industrial-gloves#breadcrumb`,
    }),
    {
      '@type': 'BreadcrumbList',
      '@id': `${SCHEMA_SITE_URL}/industrial-gloves#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Industrial Gloves', item: `${SITE_URL}/industrial-gloves` },
      ],
    },
    {
      '@type': 'Product',
      '@id': `${SCHEMA_SITE_URL}/industrial-gloves#product`,
      'name': 'Industrial Gloves Wholesale Oman — Heavy Duty PPE',
      'description': 'Wholesale heavy duty industrial PPE gloves for factories and refineries in Oman and GCC. Cut resistant, chemical barrier, anti-static ESD and general industrial nitrile. Est. 1996.',
      'image': 'https://public.readdy.ai/ai/img_res/517050174bcf810140920427385f6a6a.jpg',
      'brand': { '@type': 'Brand', 'name': 'Oman Disposables' },
      'category': 'Industrial PPE Gloves',
      'url': `${SCHEMA_SITE_URL}/industrial-gloves`,
      'offers': {
        '@type': 'Offer',
        'priceCurrency': 'OMR',
        'availability': 'https://schema.org/InStock',
        'areaServed': ['OM', 'AE', 'SA', 'KW', 'BH', 'QA'],
        'seller': { '@type': 'Organization', 'name': 'Oman Disposables' },
      },
    },
    {
      '@type': 'ItemList',
      name: 'Industrial Gloves Range — omandisposables.com',
      description: 'Heavy duty, cut-resistant, anti-static and chemical PPE gloves for factories and industrial facilities in Oman and GCC.',
      url: `${SITE_URL}/industrial-gloves`,
      numberOfItems: 4,
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          item: {
            '@type': 'Product',
            name: 'Heavy Duty Industrial Nitrile Gloves',
            description: 'Extra-thick nitrile gloves for demanding factory environments. Oil, grease and chemical resistant.',
            image: 'https://readdy.ai/api/search-image?query=factory%20worker%20wearing%20thick%20black%20industrial%20nitrile%20gloves%20working%20with%20heavy%20machinery%20in%20manufacturing%20plant%2C%20industrial%20environment%20with%20metal%20equipment%2C%20safety%20protective%20wear%20in%20operational%20factory%20setting&width=600&height=420&seq=ind-nitrile-01&orientation=landscape',
            url: `${SITE_URL}/industrial-gloves`,
            category: 'Industrial PPE Gloves',
            brand: { '@type': 'Brand', name: 'omandisposables.com' },
            offers: { '@type': 'Offer', seller: { '@type': 'Organization', name: 'omandisposables.com' }, availability: 'https://schema.org/InStock', priceCurrency: 'OMR', areaServed: ['OM', 'AE', 'SA', 'KW', 'BH', 'QA'] },
          },
        },
        {
          '@type': 'ListItem',
          position: 2,
          item: {
            '@type': 'Product',
            name: 'Cut-Resistant Gloves',
            description: 'Cut and abrasion resistant gloves for metal handling and sharp edge work.',
            image: 'https://readdy.ai/api/search-image?query=industrial%20worker%20wearing%20cut%20resistant%20safety%20gloves%20handling%20sharp%20metal%20sheets%20in%20steel%20fabrication%20workshop%2C%20protective%20equipment%20in%20metal%20processing%20factory%20with%20sparks%20and%20heavy%20equipment&width=600&height=420&seq=ind-cut-01&orientation=landscape',
            url: `${SITE_URL}/industrial-gloves`,
            category: 'Industrial PPE Gloves',
            brand: { '@type': 'Brand', name: 'omandisposables.com' },
            offers: { '@type': 'Offer', seller: { '@type': 'Organization', name: 'omandisposables.com' }, availability: 'https://schema.org/InStock', priceCurrency: 'OMR', areaServed: ['OM', 'AE', 'SA', 'KW', 'BH', 'QA'] },
          },
        },
        {
          '@type': 'ListItem',
          position: 3,
          item: {
            '@type': 'Product',
            name: 'Anti-Static ESD Gloves',
            description: 'Static-dissipative gloves for electronics assembly and PCB handling.',
            image: 'https://readdy.ai/api/search-image?query=electronics%20technician%20wearing%20anti-static%20ESD%20gloves%20carefully%20assembling%20circuit%20board%20in%20electronics%20manufacturing%20facility%2C%20clean%20white%20workspace%20with%20electronic%20components%20and%20soldering%20equipment&width=600&height=420&seq=ind-esd-01&orientation=landscape',
            url: `${SITE_URL}/industrial-gloves`,
            category: 'Industrial PPE Gloves',
            brand: { '@type': 'Brand', name: 'omandisposables.com' },
            offers: { '@type': 'Offer', seller: { '@type': 'Organization', name: 'omandisposables.com' }, availability: 'https://schema.org/InStock', priceCurrency: 'OMR', areaServed: ['OM', 'AE', 'SA', 'KW', 'BH', 'QA'] },
          },
        },
        {
          '@type': 'ListItem',
          position: 4,
          item: {
            '@type': 'Product',
            name: 'Chemical Splash PPE Gloves',
            description: 'Industrial PPE nitrile gloves for chemical plant and refinery environments.',
            image: 'https://readdy.ai/api/search-image?query=chemical%20plant%20worker%20in%20full%20PPE%20wearing%20long-cuff%20industrial%20protective%20gloves%20handling%20chemical%20containers%20in%20oil%20refinery%20facility%2C%20safety%20warning%20signs%20and%20industrial%20piping%20in%20background&width=600&height=420&seq=ind-chem-01&orientation=landscape',
            url: `${SITE_URL}/industrial-gloves`,
            category: 'Industrial PPE Gloves',
            brand: { '@type': 'Brand', name: 'omandisposables.com' },
            offers: { '@type': 'Offer', seller: { '@type': 'Organization', name: 'omandisposables.com' }, availability: 'https://schema.org/InStock', priceCurrency: 'OMR', areaServed: ['OM', 'AE', 'SA', 'KW', 'BH', 'QA'] },
          },
        },
      ],
    },
  ],
};

const PRODUCTS = [
  {
    name: 'Heavy Duty Industrial Nitrile',
    nameAr: 'نيتريل صناعي ثقيل',
    desc: 'Extra-thick nitrile gloves for demanding factory environments. Superior oil, grease and chemical resistance for shift-length wear.',
    descAr: 'قفازات نيتريل سميكة للغاية لبيئات المصانع المتطلبة. مقاومة فائقة للزيوت والشحوم والمواد الكيميائية لارتداء طويل.',
    specs: ['0.20mm+ Thickness', 'Oil & Grease Resistant', 'Chemical Resistant', 'Black / Blue'],
    img: 'https://readdy.ai/api/search-image?query=factory%20worker%20wearing%20thick%20black%20industrial%20nitrile%20gloves%20working%20with%20heavy%20machinery%20in%20manufacturing%20plant%2C%20industrial%20environment%20with%20metal%20equipment%2C%20safety%20protective%20wear%20in%20operational%20factory%20setting&width=600&height=420&seq=ind-nitrile-01&orientation=landscape',
    tag: 'Heavy Duty',
  },
  {
    name: 'Cut-Resistant Gloves',
    nameAr: 'قفازات مقاومة للقطع',
    desc: 'Cut and abrasion resistant disposable gloves. For metal handling, glass processing and sharp edge work.',
    descAr: 'قفازات مقاومة للقطع والتآكل. لمناولة المعادن ومعالجة الزجاج والعمل مع الحواف الحادة.',
    specs: ['Cut Resistant', 'Cut Level 3+', 'Abrasion Resistant', 'Reinforced Cuff'],
    img: 'https://readdy.ai/api/search-image?query=industrial%20worker%20wearing%20cut%20resistant%20safety%20gloves%20handling%20sharp%20metal%20sheets%20in%20steel%20fabrication%20workshop%2C%20protective%20equipment%20in%20metal%20processing%20factory%20with%20sparks%20and%20heavy%20equipment&width=600&height=420&seq=ind-cut-01&orientation=landscape',
    tag: 'Cut Resistant',
  },
  {
    name: 'Anti-Static ESD Gloves',
    nameAr: 'قفازات مضادة للكهرباء الساكنة',
    desc: 'Static-dissipative gloves for electronics assembly, PCB handling and sensitive component manufacturing. Prevents ESD damage.',
    descAr: 'قفازات مبددة للكهرباء الساكنة لتجميع الإلكترونيات ومناولة لوحات الدوائر وتصنيع المكونات الحساسة. تمنع أضرار التفريغ الكهروستاتيكي.',
    specs: ['ESD Rated', 'Carbon-Tipped', 'Static-Dissipative', 'Electronics Safe'],
    img: 'https://readdy.ai/api/search-image?query=electronics%20technician%20wearing%20anti-static%20ESD%20gloves%20carefully%20assembling%20circuit%20board%20in%20electronics%20manufacturing%20facility%2C%20clean%20white%20workspace%20with%20electronic%20components%20and%20soldering%20equipment&width=600&height=420&seq=ind-esd-01&orientation=landscape',
    tag: 'Anti-Static ESD',
  },
  {
    name: 'Chemical Splash PPE Gloves',
    nameAr: 'قفازات معدات الوقاية الشخصية الكيميائية',
    desc: 'Industrial PPE nitrile gloves for chemical plant and refinery environments. Tested against aggressive chemicals.',
    descAr: 'قفازات نيتريل للوقاية الشخصية في بيئات المصانع الكيميائية والمصافي. مختبرة ضد المواد الكيميائية العدوانية.',
    specs: ['Chemical Tested', 'Refinery Grade', 'Long Cuff 30cm', 'Heavy Duty Grade'],
    img: 'https://readdy.ai/api/search-image?query=chemical%20plant%20worker%20in%20full%20PPE%20wearing%20long-cuff%20industrial%20protective%20gloves%20handling%20chemical%20containers%20in%20oil%20refinery%20facility%2C%20safety%20warning%20signs%20and%20industrial%20piping%20in%20background&width=600&height=420&seq=ind-chem-01&orientation=landscape',
    tag: 'Chemical Grade',
  },
];

const IND_FEATURES = [
  { icon: 'ri-sword-line', en: 'Cut Resistant', ar: 'مقاوم للقطع' },
  { icon: 'ri-flask-line', en: 'Chemical Barrier', ar: 'حاجز كيميائي' },
  { icon: 'ri-flashlight-line', en: 'Anti-Static ESD', ar: 'مضاد للكهرباء الساكنة' },
  { icon: 'ri-drop-line', en: 'Oil & Grease Proof', ar: 'مقاوم للزيت والشحوم' },
  { icon: 'ri-shield-star-line', en: 'Heavy Duty Grade', ar: 'درجة ثقيلة' },
  { icon: 'ri-stack-line', en: 'Bulk Factory Supply', ar: 'توريد مصنعي بالجملة' },
];

export default function IndustrialGlovesPage() {
  return (
    <main>
      <title>Industrial Gloves Wholesale Oman | Heavy Duty PPE Gloves | omandisposables.com</title>
      <meta name="description" content="Heavy duty industrial PPE gloves wholesale for factories in Oman & GCC. Cut resistant, chemical barrier, anti-static ESD. Bulk supply from Oman." />
      <meta name="keywords" content="industrial gloves Oman, cut resistant gloves, PPE gloves wholesale Muscat, chemical resistant gloves GCC, factory gloves supplier" />
      <link rel="canonical" href={`${SITE_URL}/industrial-gloves`} />
      <meta property="og:title" content="Industrial Gloves Wholesale Oman | Heavy Duty PPE | omandisposables.com" />
      <meta property="og:description" content="Heavy duty industrial PPE gloves wholesale for factories in Oman & GCC. Cut resistant, chemical barrier, anti-static ESD. Trusted supply." />
      <meta property="og:url" content={`${SITE_URL}/industrial-gloves`} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content="https://readdy.ai/api/search-image?query=industrial%20factory%20workers%20wearing%20protective%20nitrile%20gloves%20operating%20heavy%20machinery%20in%20large%20manufacturing%20plant%2C%20strong%20directional%20lighting%20creating%20dramatic%20shadows%20on%20factory%20floor%20with%20machinery%20and%20conveyor%20belts&width=1200&height=630&seq=ind-hero-og&orientation=landscape" />
      <meta property="og:site_name" content="omandisposables.com" />
      <meta property="og:locale" content="en_US" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Industrial Gloves Wholesale Oman | omandisposables.com" />
      <meta name="twitter:description" content="Cut resistant, chemical barrier, anti-static ESD industrial gloves wholesale for factories in Oman & GCC." />
      <meta name="twitter:image" content="https://readdy.ai/api/search-image?query=industrial%20factory%20workers%20wearing%20protective%20nitrile%20gloves%20operating%20heavy%20machinery%20in%20large%20manufacturing%20plant%2C%20strong%20directional%20lighting%20creating%20dramatic%20shadows%20on%20factory%20floor%20with%20machinery%20and%20conveyor%20belts&width=1200&height=630&seq=ind-hero-og&orientation=landscape" />
      <meta name="twitter:site" content="@omandisposables" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(PAGE_SCHEMA) }}
      />

      <Navbar />

      {/* Hero */}
      <section className="relative pt-24 pb-16 bg-navy overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=industrial%20factory%20workers%20wearing%20protective%20nitrile%20gloves%20operating%20heavy%20machinery%20in%20large%20manufacturing%20plant%2C%20strong%20directional%20lighting%20creating%20dramatic%20shadows%20on%20factory%20floor%20with%20machinery%20and%20conveyor%20belts&width=1920&height=600&seq=ind-hero-01&orientation=landscape"
            alt="Industrial Gloves Wholesale Oman - omandisposables.com"
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy/95 to-navy/60" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
          <div className="inline-flex items-center gap-2 bg-brand-green/20 border border-brand-green/40 text-brand-green text-sm font-semibold px-4 py-2 rounded-full mb-6">
            🏅 Trusted Industrial Gloves Supplier from Oman
          </div>
          <h1 className="font-heading font-black text-white text-4xl md:text-5xl lg:text-6xl mb-3">
            Industrial Gloves Wholesale
          </h1>
          <p className="font-arabic text-white/80 text-2xl md:text-3xl font-bold mb-4" dir="rtl">
            القفازات الصناعية بالجملة
          </p>
          <p className="text-white/75 text-base md:text-lg max-w-2xl mb-4">
            Heavy duty, cut-resistant, anti-static and chemical PPE gloves for factories, refineries
            and industrial facilities across Oman and GCC.
          </p>
          <p className="font-arabic text-white/65 text-base max-w-2xl mb-8 text-right" dir="rtl">
            قفازات ثقيلة ومقاومة للقطع ومضادة للكهرباء الساكنة وقفازات PPE الكيميائية للمصانع
            والمصافي والمنشآت الصناعية في عُمان والخليج.
          </p>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-whatsapp text-base px-8 py-4 rounded-full shadow-xl hover:scale-105 transition-transform inline-flex"
          >
            <i className="ri-whatsapp-line text-xl" />
            💬 WhatsApp for Industrial Gloves Pricing
          </a>
        </div>
      </section>

      {/* Trust bar */}
      <div className="bg-brand-green py-3 px-4">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-center gap-6 text-white text-sm font-semibold">
          {['Cut Resistant', 'Chemical Barrier', 'Heavy Duty', 'Anti-Static ESD', 'Factory Ready'].map((t) => (
            <span key={t} className="flex items-center gap-1">
              <i className="ri-checkbox-circle-fill" />{t}
            </span>
          ))}
        </div>
      </div>

      {/* Products */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center mb-12">
            <span className="trust-badge mb-4 inline-flex">🏅 Built for Oman&apos;s Industry.</span>
            <h2 className="section-title-en text-3xl md:text-4xl mb-2">Our Industrial Glove Range</h2>
            <p className="section-title-ar text-2xl md:text-3xl" dir="rtl">مجموعة القفازات الصناعية لدينا</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-product-shop>
            {PRODUCTS.map((p) => (
              <div key={p.name} className="bg-white border border-gray-100 rounded-xl overflow-hidden hover:border-brand-green/30 hover:shadow-md transition-all">
                <div className="w-full h-52 overflow-hidden">
                  <img src={p.img} alt={`${p.name} - omandisposables.com Oman`} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <h3 className="font-heading font-bold text-navy text-lg">{p.name}</h3>
                      <p className="font-arabic text-gray-500 text-sm" dir="rtl">{p.nameAr}</p>
                    </div>
                    <span className="shrink-0 bg-brand-green/10 text-brand-green text-xs font-semibold px-3 py-1 rounded-full">{p.tag}</span>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed mb-2">{p.desc}</p>
                  <p className="font-arabic text-gray-500 text-xs text-right mb-4 leading-relaxed" dir="rtl">{p.descAr}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {p.specs.map((s) => (
                      <span key={s} className="text-xs bg-gray-50 border border-gray-200 text-gray-600 px-2.5 py-1 rounded-full">{s}</span>
                    ))}
                  </div>
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-whatsapp text-sm px-4 py-2.5 rounded-full w-full justify-center"
                  >
                    <i className="ri-whatsapp-line" />
                    Get Price &amp; Availability
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section-padding bg-gray-50">
        <div className="container-max">
          <div className="text-center mb-10">
            <h2 className="section-title-en text-2xl md:text-3xl mb-2">Industrial Protection Features</h2>
            <p className="section-title-ar text-xl md:text-2xl" dir="rtl">مميزات الحماية الصناعية</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {IND_FEATURES.map((f) => (
              <div key={f.en} className="flex flex-col items-center text-center p-5 bg-white rounded-xl border border-gray-100">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-navy/5 mb-3">
                  <i className={`${f.icon} text-2xl text-navy`} />
                </div>
                <p className="font-heading font-semibold text-navy text-xs">{f.en}</p>
                <p className="font-arabic text-gray-400 text-xs mt-0.5" dir="rtl">{f.ar}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-navy text-center">
        <div className="container-max">
          <div className="inline-flex items-center gap-2 bg-brand-green/20 border border-brand-green/40 text-brand-green text-sm font-semibold px-4 py-2 rounded-full mb-6">
            🏅 جودة موثوقة. راسخون في عُمان. | Quality. Rooted in Oman.
          </div>
          <h2 className="font-heading font-black text-white text-3xl md:text-4xl mb-3">
            Order Industrial Gloves Wholesale
          </h2>
          <p className="font-arabic text-white/80 text-xl font-bold mb-6" dir="rtl">
            طلب القفازات الصناعية بالجملة
          </p>
          <p className="text-white/70 text-base max-w-lg mx-auto mb-8">
            Factories, refineries, construction and industrial sites — get bulk pricing and PPE quotes via WhatsApp. 24-hour response guaranteed.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp text-base px-8 py-4 rounded-full shadow-xl hover:scale-105 transition-transform w-full sm:w-auto justify-center"
            >
              <i className="ri-whatsapp-line text-xl" />
              💬 WhatsApp for Wholesale Price
            </a>
            <Link
              to="/request-quote"
              className="bg-white/10 border border-white/20 text-white font-heading font-semibold px-8 py-4 rounded-full hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer w-full sm:w-auto flex items-center justify-center gap-2"
            >
              📋 Request a Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}
